import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './CrosswalksChart.css';

const blockProportion = 0.85;
const xPadding = 80;
const yPadding = 25;
const innerDim = 500;
const xDim = innerDim + 2 * xPadding;
const yDim = innerDim + 2 * yPadding;
const vertexOpacity = 1.0;
const maxWait = 151.3;
const tooltipShiftProportion = 0.7;
const textLineHeight = 16;
const defaultRadius = 7;
const maxRadius = 8;

class CrosswalksChart extends React.Component {
  static propTypes = {
    vertexStyle: PropTypes.string,
    edgeStyle: PropTypes.string,
    id: PropTypes.string.isRequired,
  }

  componentDidMount() {
    d3.json('/posts/crosswalks/data.json', (data) => {
      const maxBlocks = this.props.edgeStyle === 'chance' ?
        5 : 10;
      this.numBlocks = Math.min(data.wait.nw.length, maxBlocks);
      this.blockSize = innerDim / this.numBlocks;
      this.blocks = [];
      Object.keys(data.wait).forEach((direction) => {
        const grid = data.wait[direction];
        grid.forEach((row, i) => {
          row.forEach((wait, j) => {
            if (i >= maxBlocks || j >= maxBlocks) {
              return;
            }
            this.blocks.push({
              chance: data.chance[direction][i][j],
              chanceHoriz: data.chance_horiz[direction][i][j],
              chanceVerti: data.chance_verti[direction][i][j],
              chanceDiago: data.chance_diago[direction][i][j],
              wait,
              direction,
              i,
              j,
            });
          });
        });
      });

      this.drawGraph();
    });
  }

  shouldComponentUpdate() {
    return false;
  }

  getParentId() {
    return `crosswalks-chart-${this.props.id}`;
  }

  getChartId() {
    return `crosswalks-svg-${this.props.id}`;
  }

  getXOff(direction) {
    const off = direction[1] === 'e' ? 0.75 : 0.25;
    return off - (0.5 - blockProportion / 2);
  }

  getYOff(direction) {
    const off = direction[0] === 's' ? 0.75 : 0.25;
    return off - (0.5 - blockProportion / 2);
  }

  getTooltipLines(d) {
    const lines = [];
    if (this.props.edgeStyle === 'chance') {
      const { chanceHoriz, chanceVerti, chanceDiago } = d;
      if (chanceVerti > 0) {
        lines.push(`chance to go north: ${chanceVerti.toFixed(2)}`);
      }
      if (chanceHoriz > 0) {
        lines.push(`chance to go west: ${chanceHoriz.toFixed(2)}`);
      }
      if (chanceDiago > 0) {
        lines.push(`chance to go diagonally: ${chanceDiago.toFixed(2)}`);
      }
    }
    if (this.props.vertexStyle === 'wait') {
      lines.push(`expected wait: ${d.wait.toFixed(1)}`);
    } else if (this.props.vertexStyle === 'chance') {
      lines.push(`chance of reaching: ${d.chance.toFixed(2)}`);
    }
    return lines.map((text, number) => ({ text, number }));
  }

  idFromData(category, d, selector) {
    const id = `${this.props.id}-${category}-${d.direction}-${d.i}-${d.j}`;
    return selector ? `#${id}` : id;
  }

  vertexColor(d) {
    const fraction = d.wait / maxWait;
    const grayness = Math.floor(255 * (1 - fraction));
    return `rgb(${255},${grayness},${grayness})`;
  }

  edgeWidth(d, category) {
    return 3 * d[category];
  }

  vertexRadius(d) {
    if (this.props.vertexStyle !== 'chance') {
      return defaultRadius;
    }
    return maxRadius * Math.sqrt(d.chance);
  }

  drawGraph() {
    if (this.svg) {
      d3.select(`#${this.getChartId()}`).remove();
    }
    this.svg = d3.select(`#${this.getParentId()}`)
      .append('svg')
      .attr('id', () => this.getChartId())
      .attr('width', xDim)
      .attr('height', yDim)
      .append('g')
      .attr('transform', `translate(${xPadding},${yPadding})`)
      .attr('width', innerDim)
      .attr('height', innerDim);
    this.xScale = d3.scaleLinear()
      .domain([0, this.numBlocks])
      .range([0, innerDim]);
    this.yScale = d3.scaleLinear()
      .domain([0, this.numBlocks])
      .range([0, innerDim]);
    this.xAxis = d3.axisBottom()
      .scale(this.xScale);
    this.yAxis = d3.axisLeft()
      .scale(this.yScale);

    this.curbs = this.svg
      .selectAll(`.${s.curb}`)
      .data(this.blocks)
      .enter()
      .append('line')
      .attr('id', (d) => this.idFromData('curb', d, false))
      .attr('class', s.curb)
      .attr('x1', (d) => this.xScale(d.i + (d.direction[0] === 'n') * blockProportion))
      .attr('y1', (d) => this.yScale(d.j + (d.direction[1] === 'w') * blockProportion))
      .attr('x2', (d) => this.xScale(d.i + (d.direction[1] === 'e') * blockProportion))
      .attr('y2', (d) => this.yScale(d.j + (d.direction[0] === 'n') * blockProportion));
    this.boxes = this.svg
      .selectAll(`.${s.box}`)
      .data(this.blocks)
      .enter()
      .append('g')
      .attr('id', (d) => this.idFromData('box', d, false))
      .attr('class', s.box)
      .attr('transform',
        (d) => `translate(${this.coordTransformX(d)},${this.coordTransformY(d)})`,
      );
    this.vertices = this.boxes
      .append('circle')
      .attr('id', (d) => this.idFromData('vertex', d, false))
      .attr('class', s.vertex)
      .attr('r', (d) => this.vertexRadius(d))
      .attr('fill', (d) => this.vertexColor(d))
      .attr('opacity', vertexOpacity)
      .on('mouseover', (d) => this.handleMouseover(d))
      .on('mouseout', (d) => this.handleMouseout(d));
    if (this.props.edgeStyle === 'chance') {
      this.horizEdges = this.boxes
        .append('line')
        .attr('class', s.edge)
        .attr('x1', -maxRadius)
        .attr('y1', 0)
        .attr('x2', -(this.blockSize / 2 - maxRadius))
        .attr('y2', 0)
        .attr('stroke-width', (d) => this.edgeWidth(d, 'chanceHoriz'));
      this.vertiEdges = this.boxes
        .append('line')
        .attr('class', s.edge)
        .attr('x1', 0)
        .attr('y1', -maxRadius)
        .attr('x2', 0)
        .attr('y2', -(this.blockSize / 2 - maxRadius))
        .attr('stroke-width', (d) => this.edgeWidth(d, 'chanceVerti'));
      this.diagoEdges = this.boxes
        .append('line')
        .attr('class', s.edge)
        .attr('x1', -maxRadius / Math.sqrt(2))
        .attr('y1', -maxRadius / Math.sqrt(2))
        .attr('x2', -(this.blockSize / 2 - maxRadius / Math.sqrt(2)))
        .attr('y2', -(this.blockSize / 2 - maxRadius / Math.sqrt(2)))
        .attr('stroke-width', (d) => this.edgeWidth(d, 'chanceDiago'));
    }

    this.tooltip = this.svg
      .append('g')
      .attr('id', `${this.props.id}-tooltip`)
      .attr('class', s.tooltip)
      .style('display', 'none');
    this.tooltipRect = this.tooltip
      .append('rect')
      .attr('fill', 'white')
      .attr('stroke', 'black')
      .attr('stroke-width', 1.5)
      .attr('x', 3);
    this.tooltipText = this.tooltip
      .append('text');
    this.destination = this.svg
      .append('text')
      .text('destination')
      .attr('class', s.destination)
      .attr('x', -40)
      .attr('y', -7);
  }

  coordTransformX(d) {
    const x = d.i + this.getXOff(d.direction);
    return this.xScale(x);
  }

  coordTransformY(d) {
    const y = d.j + this.getYOff(d.direction);
    return this.yScale(y);
  }

  handleMouseover(d) {
    const vertex = d3.select(this.idFromData('vertex', d, true));
    vertex
      .attr('opacity', 1.0);

    const lines = this.getTooltipLines(d);
    const maxLength = Math.max(...lines.map((line) => line.text.length));
    const tooltipWidth = maxLength * 8;
    const tooltipHeight = 8 + textLineHeight * lines.length;
    const tooltipXOffset = d.i >= tooltipShiftProportion * this.numBlocks ?
      -tooltipWidth :
      0;
    const tooltipYOffset = d.j === 0 ?
      tooltipHeight :
      0;

    this.tooltip
      .attr('transform',
        `translate(${this.coordTransformX(d) + tooltipXOffset},${this.coordTransformY(d) + tooltipYOffset})`,
      );
    this.tooltipRect
      .attr('width', tooltipWidth)
      .attr('height', tooltipHeight)
      .attr('y', -14 - textLineHeight * lines.length);
    this.tooltipText
      .selectAll('tspan')
      .remove();
    this.tooltipText
      .selectAll('tspan')
      .data(lines)
      .enter()
      .append('tspan')
      .text((line) => line.text)
      .attr('x', 8)
      .attr('y', (line) => -12 - line.number * textLineHeight);
    this.tooltip
      .style('display', 'initial');
  }

  handleMouseout(d) {
    const vertex = d3.select(this.idFromData('vertex', d, true));
    vertex
      .attr('opacity', vertexOpacity);
    this.tooltip
      .style('display', 'none');
  }

  render() {
    return (
      <div id={this.getParentId()} /* className={s.container}*/ />
    );
  }
}

export default withStyles(s)(CrosswalksChart);



// WEBPACK FOOTER //
// src/components/CrosswalksChart/CrosswalksChart.js